import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from '@iconify/react';
import HistoryIcon from "@mui/icons-material/History";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, CircularProgress } from '@mui/material';
import { useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { ControlledTextField } from 'src/components/forms';
import { useLoadOptions } from 'src/hooks/useLoadOptions';
import CardActions from 'src/pages/sistema/components/CardActions';

import { useAFOPUAforoTipos } from '../hooks/useAFOPUCache';
import { useAFOPUTablaValor } from '../hooks/useTablaValorAFOPU';
import { FormUpdateAFOPUTablaValores, schemaFormUpdateAFOPUTablaValores } from '../types/dto';
import { OperacionAFOPU } from '../types/types';

import { useSubmitOperacion } from 'src/pages/sistema/hooks/useSubmitOperacion';
import { ContextOperacion, ContextOperacionType } from 'src/pages/sistema/RefactorLayout';
import { EntityWithIdFields } from 'src/types';
import { PermissionType } from 'src/types/security';
import { HttpAFOPU } from '../api/httoAFOPU';
import AFOPUTablaValores from './AFOPUTablaValores';
import AFOPUTablaValoresEditorGrid from './AFOPUTablaValoresEditorGrid';
import { AFOPUTablaValoresHistoricoDialog } from './AFOPUTablaValoresHistoricoDialog';

interface SeccionAFOPUTablaValoresProps {
  operacion: OperacionAFOPU;
  codSistemaPrincipal: number;
}

const SeccionAFOPUTablaValores = ({ operacion, codSistemaPrincipal }: SeccionAFOPUTablaValoresProps) => {
  const { permisoTipo } = useContext<ContextOperacionType>(ContextOperacion)

  const [isEditing, setIsEditing] = useState(false);
  const [historialDialogOpen, setHistorialDialogOpen] = useState(false);

  const { submitOperacion } = useSubmitOperacion();

  const { headers, rows, aforos, isLoading, refetch, error } = useAFOPUTablaValor(
    operacion[EntityWithIdFields.Id]
  );

  const aforoTipos = useAFOPUAforoTipos(codSistemaPrincipal);
  const loadAforoTipos = useLoadOptions({
    data: aforoTipos.aforoTipos,
    loading: aforoTipos.isLoading,
    isError: aforoTipos.isError,
    error: aforoTipos.error
  });

  const methods = useForm<FormUpdateAFOPUTablaValores>({
    resolver: zodResolver(schemaFormUpdateAFOPUTablaValores),
    defaultValues: {
      observaciones: '',
      tablaValores: []
    }
  });

  const handleEdit = () => {
    methods.reset({
      observaciones: '',
      tablaValores: aforos as any
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    methods.reset();
    setIsEditing(false);
  };

  const onSave = async (data: FormUpdateAFOPUTablaValores) => {
    await submitOperacion(data, {
      apiCall: (payload) => HttpAFOPU.updateTablaValores(operacion[EntityWithIdFields.Id], payload),
      successMessage: 'Tabla de valores actualizada correctamente',
      navigateTo: null,
      onSuccess: async () => {
        await refetch();
        setIsEditing(false);
      }
    });
  };

  return (
    <CardActions
      title='Tabla de Valores'
      icon='mdi:table-check'
      colapsable
      tableContent
      actions={
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setHistorialDialogOpen(true)}
            sx={{ textTransform: 'none' }}
            variant={"contained"}
          // disabled={statusHistoria === QueryStatus.Loading}
          >
            Historial
          </Button>

          {permisoTipo >= PermissionType.Write && (
            !isEditing ? (
              <Button
                variant="outlined"
                size="small"
                onClick={handleEdit}
                startIcon={<Icon icon="mdi:pencil" />}
              >
                Editar
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outlined" color="secondary" size="small" onClick={handleCancel}>
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={methods.handleSubmit(onSave)}
                  startIcon={<SaveIcon />}
                >
                  Guardar Cambios
                </Button>
              </Box>
            )
          )}
        </Box>
      }
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>
      ) : isEditing ? (
        <FormProvider {...methods}>
          <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <ControlledTextField
              name="observaciones"
              control={methods.control}
              label="Observaciones"
              multiline
              rows={2}
              fullWidth
            />
            <AFOPUTablaValoresEditorGrid
              formMethods={methods}
              headers={headers}
              rows={rows}
              aforos={methods.getValues('tablaValores')}
              loadAforoTipos={loadAforoTipos}
              isLoading={false}
              permiteEditar={true}
            />
          </Box>
        </FormProvider>
      ) : (
        <AFOPUTablaValores headers={headers} rows={rows} loading={isLoading} error={error} />
      )}

      {historialDialogOpen && (
        <AFOPUTablaValoresHistoricoDialog
          idOperacion={operacion[EntityWithIdFields.Id]}
          open={historialDialogOpen}
          onClose={() => setHistorialDialogOpen(false)}
        />
      )}
    </CardActions>
  );
};

export default SeccionAFOPUTablaValores;